// Consent safe event helper.
//
// PostHog now runs cookieless-until-consent (see src/posthog.js): the client is
// loaded for every visitor with memory-only persistence, so events flow
// anonymously before a banner choice too. `__loaded` still gates the two cases
// where no client exists at all — a stored decline, localhost, and marked
// internal devices — and after an in-page decline PostHog's own opt-out drops
// captures internally.
//
// Returns true when the event was handed to PostHog, false when it was dropped.

import posthog from '../src/posthog.js';
import { onConsentChange } from './consent.js';

export function track(event, properties, options) {
  try {
    if (!posthog.__loaded) return false;
    posthog.capture(event, properties, options);
    return true;
  } catch (_) {
    return false;
  }
}

// For events fired as the page is being replaced (a link click that navigates
// away): sendBeacon survives unload, a normal XHR is cancelled with the document.
export function trackOnExit(event, properties) {
  return track(event, properties, { transport: 'sendBeacon' });
}

/**
 * For events that describe the page load itself — the outcome of a sign in, say
 * — which happen before the visitor has had a chance to answer the cookie bar.
 * Dropping them outright would lose the event on every first visit, so it is
 * held until consent is granted on this same page and then sent, exactly like
 * the pageview PostHog itself records on init.
 *
 * Nothing is persisted: navigate away or refuse, and the event is gone.
 */
export function trackOnConsent(event, properties) {
  if (track(event, properties)) return;

  const stop = onConsentChange((consent) => {
    if (!consent.analytics) return;
    stop();
    // src/posthog.js starts the client from its own listener; yield a tick so
    // it is initialised regardless of the order the two listeners run in.
    setTimeout(() => track(event, properties), 0);
  });
}
